import { AuthResponse } from 'types/response';

export const AUTH_TOKEN_CHANGED_EVENT = 'auth:token-changed';
export const REFRESH_TOKEN_COOKIE_NAME = 'refresh';

export const setRefreshCookie = (token: string): void => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${REFRESH_TOKEN_COOKIE_NAME}=${token}; path=/; SameSite=Lax${secure}; Max-Age=172800`;
};

export const getAuthenticatedUserInfo = async (
  result: AuthResponse['AuthenticationResult']
): Promise<AuthResponse['userProfile']> => {
  const response: Response = await fetch('/api/auth/me', {
    method: 'GET',
    headers: [
      ['Authorization', `Bearer ${result.accessToken}`],
      ['Content-Type', 'application/json']
    ]
  });

  switch (response.status) {
    case 200:
      return await response.json();
    case 401:
      throw new Error('Invalid username or password');
    default:
      throw new Error(response.statusText);
  }
};

export const requestToken = async (code: string): Promise<AuthResponse['AuthenticationResult'] | string> => {
  try {
    const response: Response = await fetch(`${process.env.REACT_APP_COGNITO_URI}/oauth2/token`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/x-www-form-urlencoded'
      }),
      body: [
        encodeURIComponent('grant_type') + '=' + encodeURIComponent('authorization_code'),
        encodeURIComponent('code') + '=' + encodeURIComponent(code),
        encodeURIComponent('redirect_uri') + '=' + encodeURIComponent(process.env.REACT_APP_REDIRECT_URI as string),
        encodeURIComponent('client_id') + '=' + encodeURIComponent(process.env.REACT_APP_COGNITO_CLIENT_ID as string)
      ].join('&')
    });
    switch (response.status) {
      case 200:
        return Object.fromEntries(Object.entries(await response.json()).map(([k, v]) => [snakeToPascal(k), v])) as any;
      case 401:
        throw new Error('Invalid authorization code');
      default:
        throw new Error(response.statusText);
    }
  } catch (e) {
    if (e instanceof Error) return e.message;
    return 'Error';
  }
};

let inflightRefresh: Promise<string | null> | null = null;

export const refreshToken = async (): Promise<string | null> => {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async (): Promise<string | null> => {
    try {
      const token = document.cookie.match(/refresh=([^;]+)/)?.[1];
      if (!token) throw new Error('Unauthorized');
      const response: Response = await fetch(`${process.env.REACT_APP_COGNITO_URI}/oauth2/token`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/x-www-form-urlencoded'
        }),
        body: [
          encodeURIComponent('grant_type') + '=' + encodeURIComponent('refresh_token'),
          encodeURIComponent('client_id') + '=' + encodeURIComponent(process.env.REACT_APP_COGNITO_CLIENT_ID as string),
          encodeURIComponent('refresh_token') + '=' + encodeURIComponent(token)
        ].join('&')
      });
      if (response.status !== 200) throw new Error(response.statusText);
      const cognitoResponse: any = Object.fromEntries(
        Object.entries(await response.json()).map(([k, v]) => [snakeToPascal(k), v])
      );

      try {
        await getAuthenticatedUserInfo(cognitoResponse);
      } catch (e) {
        if (e instanceof Error) console.error(e.message);
        return null;
      }

      localStorage.setItem('token', cognitoResponse.accessToken);
      if (cognitoResponse.refreshToken) setRefreshCookie(cognitoResponse.refreshToken);
      window.dispatchEvent(new Event(AUTH_TOKEN_CHANGED_EVENT));
      return cognitoResponse.accessToken;
    } catch (e) {
      if (e instanceof Error) console.error(e.message);
      return null;
    }
  })();

  try {
    return await inflightRefresh;
  } finally {
    inflightRefresh = null;
  }
};

export const logoutEndpoint = async () => {
  try {
    await fetch('/api/auth/logout', { method: 'POST' });
  } catch (e) {
    if (e instanceof Error) console.error(e.message);
  }
};

const snakeToPascal = (convert: string) =>
  convert
    .split('_')
    .map((s, idx) => idx ? s.substring(0, 1).toUpperCase() + s.substring(1) : s)
    .join('');
